/**
 * Copy-to-clipboard for the article's short link.
 *
 * `navigator.clipboard` needs a secure context. On plain http — which the dev
 * host is — it is undefined, so the fallback is not optional: without it the
 * button silently does nothing on every developer's machine and looks fine in
 * production.
 */
import { qsa, on } from './dom.js';

const FEEDBACK_MS = 2000;

export async function copy(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Fallback for non-secure contexts. execCommand is deprecated but is still
  // the only thing that works there.
  const scratch = document.createElement('textarea');
  scratch.value = text;
  scratch.setAttribute('readonly', '');
  // Off-screen rather than display:none — a hidden textarea cannot be selected.
  scratch.style.position = 'fixed';
  scratch.style.opacity = '0';
  document.body.appendChild(scratch);
  scratch.select();
  try {
    document.execCommand('copy');
  } finally {
    scratch.remove();
  }
}

/** Per-button restore timers, so two buttons cannot cancel each other's. */
const timers = new WeakMap();

export function setupClipboard() {
  const buttons = qsa('[data-copy]');
  if (!buttons.length) return;

  for (const button of buttons) {
    on(button, 'click', async (event) => {
      event.preventDefault();

      const source = document.querySelector(button.dataset.copy);
      const text = source ? (source.value ?? source.textContent) : '';
      if (!text) return;

      try {
        await copy(text.trim());
      } catch (error) {
        console.error('[clipboard]', error);
        return;
      }

      /*
       * The confirmation is the button's own label. Both strings come off the
       * element — `data-copy-done` for the confirmation, the button's current
       * text for what to restore — so the wording stays in the template and
       * translatable, and this file holds no copy.
       *
       * It replaced a sibling span that was toggled from `opacity: 0`. That span
       * occupied layout permanently to be visible for 1.6s, and in the article's
       * tools row it was taking 38px plus a gap from the short-link input, which
       * is the one thing there that needed the width.
       *
       * `restore` is captured BEFORE the swap and the timer is per-button, so a
       * second click during the window cannot leave "کپی شد" on the button
       * forever — which is what a shared timer or a re-read of textContent would
       * do.
       */
      const done = button.dataset.copyDone;
      if (!done) return;

      const restore = button.dataset.copyLabel ?? button.textContent.trim();
      button.dataset.copyLabel = restore;

      button.textContent = done;
      clearTimeout(timers.get(button));
      timers.set(
        button,
        setTimeout(() => {
          button.textContent = restore;
          timers.delete(button);
        }, FEEDBACK_MS)
      );
    });
  }
}
